import "./index.scss";

import { Box, Stack } from "@mui/material";
import Button from "components/Button";
import LazyLoader from "components/LazyLoader";
import ScreenLoader from "components/ScreenLoader";
import Tabs from "components/Tabs";
import MemberFormModal from "containers/Members/MemberFormModal";
import MessageCenter from "containers/MessageCenter";
import { TMemberDetails, TMemberTotalWorkoutStatistics } from "models/member";
import { WorkoutPlan as TWorkoutPlan } from "models/workoutplan";
import { appActions } from "modules/app";
import { getLkpGoalsList } from "modules/lookup/lookupServices";
import { getMemberById, getMemberTotalWorkoutStatistics } from "modules/member/memberServices";
import {
  getLkpFlexibilityStretchesLowerBodyList,
  getLkpFlexibilityStretchesUpperBodyList,
  getLkpPerceivedExertionList,
  getWorkoutPlanById,
} from "modules/workout-plan/workoutPlanServices";
import { memo, useCallback, useEffect, useState } from "react";
import { Location, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";

import { tabOptions } from "./data";
import Journal from "./Journal";
import MemberSummary from "./MemberSummary";
import PersonalDetails from "./PersonalDetails";
import MemberReport from "./Reports";
import { MEMBER_ROUTES } from "./routes";
import WorkoutCardModal from "./WorkoutCardModal";
import WorkoutPlanEditModal from "./WorkoutPlanEditModal";
import WorkoutPlanSection from "./WorkoutPlanSection";

const MemberDetails = () => {
  const [availableTabOptions, setAvailableTabOptions] = useState(tabOptions);
  const [activeTabIndex, setActiveTabIndex] = useState("");
  const [open, setOpen] = useState(false);
  const [currentMember, setCurrentMember] = useState<TMemberDetails>();
  const [currentMemberTotalWorkoutStatistics, setCurrentMemberTotalWorkoutStatistics] =
    useState<TMemberTotalWorkoutStatistics>();
  const [currentMemberWorkoutPlan, setCurrentMemberWorkoutPlan] = useState<TWorkoutPlan>();
  const [lkpGoalsOptions, setLkpGoalsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switchToTab, setSwitchToTab] = useState<string>();
  const [showMemberFormModal, setShowMemberFormModal] = useState(false);
  const [showEditWorkoutPlanModal, setShowEditWorkoutPlanModal] = useState(false);
  const [flexibilityUpperBodyStretchesOptions, setFlexibilityUpperBodyStretchesOptions] = useState(
    []
  );
  const [flexibilityLowerBodyStretchesOptions, setFlexibilityLowerBodyStretchesOptions] = useState(
    []
  );
  const [perceivedExertionOptions, setPerceivedExertionOptions] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const usedParams = useParams();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has("addWorkoutPlan")) {
      setShowEditWorkoutPlanModal(true);

      queryParams.delete("addWorkoutPlan");
      navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
    }
  }, []);

  const updateTabIndex = useCallback((newLocation: Location) => {
    let newReportIndex = tabOptions.findIndex((x) => newLocation.pathname.includes(x.value));
    if (newReportIndex < 0) {
      setActiveTabIndex(tabOptions[0].value);
    } else {
      setActiveTabIndex(tabOptions[newReportIndex].value);
    }
  }, []);

  const getMemberWorkoutPlan = () => {
    let promises: Promise<any>[] = [];

    promises.push(
      new Promise((resolve) => {
        getWorkoutPlanById({ member_id: id }).then((res) => {
          if (res.data) {
            setAvailableTabOptions(tabOptions);
            setCurrentMemberWorkoutPlan(res.data);
            //Get other data related to workout plan
            let subPromises: Promise<any>[] = [];

            //Get plan info goals' list
            subPromises.push(
              getLkpGoalsList().then((res) => {
                if (res.success) {
                  let goals: any[] = [];
                  Object.keys(res.data).forEach((e) => {
                    goals.push({ label: res.data[e], value: e });
                  });
                  setLkpGoalsOptions(goals);
                }
              })
            );

            //Get flexibility upper body stretches' list
            subPromises.push(
              getLkpFlexibilityStretchesUpperBodyList().then((res) => {
                if (res.success) {
                  setFlexibilityUpperBodyStretchesOptions(res.data);
                }
              })
            );

            //Get flexibility lower body stretches' list
            subPromises.push(
              getLkpFlexibilityStretchesLowerBodyList().then((res) => {
                if (res.success) {
                  setFlexibilityLowerBodyStretchesOptions(res.data);
                }
              })
            );

            //Get list for perceivedExertionOptions
            subPromises.push(
              getLkpPerceivedExertionList().then((res) => {
                if (res.success) {
                  let entries: any[] = [];
                  Object.keys(res.data).forEach((e) => {
                    entries.push({ label: `${e} - ${res.data[e]}`, value: e });
                  });
                  setPerceivedExertionOptions(entries);
                }
              })
            );

            Promise.all(subPromises).then((_v) => {
              resolve(true);
            });
          } else {
            //Hide the tabs that are empty
            const newTabOptions = availableTabOptions.filter((m) => m.value !== "workout-plan");
            setAvailableTabOptions(newTabOptions);
            setActiveTabIndex(newTabOptions[0].value);
            resolve(false);
          }
        });
      })
    );
  };

  useEffect(() => {
    updateTabIndex(location);
  }, [updateTabIndex, location]);

  useEffect(() => {
    if (currentMember) {
      dispatch(
        appActions.updatePageTitle({
          title: `${currentMember?.personal_details?.first_name} ${currentMember?.personal_details?.last_name}`,
        })
      );
    }
  }, [currentMember, dispatch]);

  //#region Preparing data needed
  useEffect(() => {
    setLoading(true);
    let promises: Promise<any>[] = [];

    //Get member data
    promises.push(
      getMemberById({ member_id: id }).then((res) => {
        setCurrentMember(res.data);
      })
    );

    //Get member total workout statistics
    promises.push(
      getMemberTotalWorkoutStatistics({ member_id: id }).then((res) => {
        setCurrentMemberTotalWorkoutStatistics(res.data);
      })
    );

    //Get member workout plan
    getMemberWorkoutPlan();

    Promise.all(promises).then((_v) => {
      setLoading(false);
    });
  }, [id]);
  //#endregion Preparing data needed

  //#region event handlers
  const openEditMemberForm = (switchToTab: string) => {
    setShowMemberFormModal(true);
    setSwitchToTab(switchToTab);
  };

  const handleCloseMemberFormModal = (isRefreshNeeded = false) => {
    setShowMemberFormModal(false);
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      setLoading(true);
      window.location.reload();
    }
  };

  const handleCloseWorkoutPlanModal = (isRefreshNeeded = false) => {
    setShowEditWorkoutPlanModal(false);
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      setLoading(true);
      window.location.reload();
    }
  };

  const handleChangeTab = useCallback((value: any) => {
    const i = tabOptions.findIndex((x) => value === x.value);
    navigate(tabOptions[i].value);
  }, []);
  //#endregion event handlers

  const renderHeaderButton = () => {
    switch (activeTabIndex) {
      case "member-summary":
        return (
          <Button
            label={"Print Workout Card"}
            color="primary-blue"
            className="margin-0"
            onClick={() => setOpen(true)}
          />
        );
      case "personal-details":
        return (
          <Stack direction={"row"} gap={1}>
            <Button
              label={"Edit"}
              className="margin-0"
              onClick={() => setShowMemberFormModal(true)}
            />{" "}
            {!currentMemberWorkoutPlan && (
              <Button
                label={"Add Workout Plan"}
                color="primary-blue"
                className="margin-0"
                onClick={() => setShowEditWorkoutPlanModal(true)}
              />
            )}
          </Stack>
        );
      case "workout-plan":
        return (
          <Stack direction="row" gap={1}>
            <Button
              label={"Print Workout Card"}
              color="primary-blue"
              className="margin-0"
              onClick={() => setOpen(true)}
            />
            <Button
              label={"Edit"}
              color="primary-blue"
              className="margin-0"
              onClick={() => setShowEditWorkoutPlanModal(true)}
            />
          </Stack>
        );
      default:
        return;
    }
  };

  return (
    <Box className={"member-details"}>
      <Tabs
        value={activeTabIndex}
        onChange={handleChangeTab}
        tabs={availableTabOptions}
        rightComp={renderHeaderButton()}
      />
      <Box>
        {loading && (
          <Box my="20px">
            <ScreenLoader isScreenLoader />
          </Box>
        )}
        <Routes>
          <Route
            path={""}
            element={
              <LazyLoader>
                {!loading && (
                  <LazyLoader>
                    {!loading && (
                      <PersonalDetails member={currentMember} editCallback={openEditMemberForm} />
                    )}
                  </LazyLoader>
                )}
              </LazyLoader>
            }
          />
          <Route
            path={MEMBER_ROUTES["member-summary"]}
            element={
              <LazyLoader>
                {!loading && (
                  <MemberSummary
                    member={currentMember}
                    memberTotalWorkoutStatistics={currentMemberTotalWorkoutStatistics}
                    workoutPlan={currentMemberWorkoutPlan}
                    goalOptions={lkpGoalsOptions}
                  />
                )}
              </LazyLoader>
            }
          />
          <Route
            path={MEMBER_ROUTES["personal-details"]}
            element={
              <LazyLoader>
                {!loading && (
                  <PersonalDetails member={currentMember} editCallback={openEditMemberForm} />
                )}
              </LazyLoader>
            }
          />
          <Route
            path={MEMBER_ROUTES["workout-plan"]}
            element={
              <LazyLoader>
                <WorkoutPlanSection
                  workoutPlan={currentMemberWorkoutPlan}
                  goalOptions={lkpGoalsOptions}
                  upperBodyStretchOptions={flexibilityUpperBodyStretchesOptions}
                  lowerBodyStretchOptions={flexibilityLowerBodyStretchesOptions}
                  perceivedExertionOptions={perceivedExertionOptions}
                />
              </LazyLoader>
            }
          />
          {/* <Route
            path={`${MEMBER_ROUTES["reports"]}/*`}
            element={
              <LazyLoader>
                <MemberReport />
              </LazyLoader>
            }
          /> */}
          <Route
            path={MEMBER_ROUTES["journal"]}
            element={
              <LazyLoader>
                <Journal />
              </LazyLoader>
            }
          />
          {/* <Route
            path={MEMBER_ROUTES["messages"]}
            element={
              <LazyLoader>
                <MessageCenter fromMemberDetails={true} />
              </LazyLoader>
            }
          /> */}
        </Routes>
      </Box>

      <WorkoutCardModal
        open={open}
        onClose={() => setOpen(false)}
        workoutPlan={currentMemberWorkoutPlan}
        perceivedExertionOptionsFromParent={perceivedExertionOptions}
        workoutPlanGoalsOptionsFromParent={lkpGoalsOptions}
        flexibilityUpperBodyStretchesOptionsFromParent={flexibilityUpperBodyStretchesOptions}
        flexibilityLowerBodyStretchesOptionsFromParent={flexibilityLowerBodyStretchesOptions}
        memberName={`${currentMember?.personal_details?.first_name} ${currentMember?.personal_details?.last_name}`}
      />

      <MemberFormModal
        open={showMemberFormModal}
        onClose={handleCloseMemberFormModal}
        memberId={parseInt(id)}
        switchToTab={switchToTab}
      />
      <WorkoutPlanEditModal
        open={showEditWorkoutPlanModal}
        onClose={handleCloseWorkoutPlanModal}
        memberId={parseInt(id)}
        memberName={`${currentMember?.personal_details?.first_name} ${currentMember?.personal_details?.last_name}`}
        memberDob={`${currentMember?.personal_details?.dob}`}
      />
    </Box>
  );
};

export default memo(MemberDetails);
